// composables/useInitApp.ts

// Type imports
import type { MenuLink, ResolvedMenuNode } from "~/types/storyblok/storyblok";
import type { Locale } from "~/types/locale";

// Regular imports
import { useFetchCategoryTree } from "~/composables/useFetchCategoryTree";
import { useLoadUser } from "~/composables/useLoadUser";
import { useStoryblokFetcher } from "~/composables/useStoryblokFetcher";
import { extractFooterFields } from "~/utils/extractFooterFields";
import { getCulturesFromLocales } from "~/utils/getCulturesFromLocales";
// `parallelProductInit` lives in layers/base/utils/ and is auto-imported by
// Nuxt across layers — no explicit import path resolves cleanly from the
// BNB template's root, so we rely on the auto-import here.

export const useInitApp = () => {
	const appStore = useAppStore();
	const productStore = useProductStore();
	const { fetchStory } = useStoryblokFetcher();
	const {
		getApplication,
		getParametricsInformation,
		getPriceLists,
		getMenuCategoriesFromNorce
	} = useNorcePwa();
	const { fetchCategoryTree } = useFetchCategoryTree();
	const { getUserByEmailIfHasSession } = useLoadUser();
	const norceStore = useNorceStore();
	const config = useRuntimeConfig();
	const nuxtApp = useNuxtApp();
	// `useCartInit` captures Nuxt composables (useRoute, useLocalePath,
	// navigateTo) here in setup so they survive the Pinia action microtask.
	const { initCart } = useCartInit();

	const initApp = async (
		iso: string,
		locale: string,
		locales: Locale[],
		storyblokLanguage?: string
	) => {
		norceStore.setCultureCode(iso);
		norceStore.setLocaleCode(locale);
		const [{ data }, categoriesTree, priceLists, , menu] = await Promise.all([
			fetchStory("layouts/main", {
				version: config.public.storyblokVersion,
				language: storyblokLanguage || iso,
				resolve_links: "url"
			}),
			fetchCategoryTree(locale),
			getPriceLists({ cultureCode: iso }),
			nuxtApp.runWithContext(() => getUserByEmailIfHasSession()),
			// Storyblok `MenuNode` menu — same call as the base useInitApp. Empty
			// (or failed) ⇒ the header falls back to the legacy menu fields.
			$fetch<ResolvedMenuNode[]>("/api/menu", {
				query: { locale: storyblokLanguage || iso, cultureCode: iso }
			}).catch((err) => {
				console.warn(
					"[useInitApp] /api/menu failed, falling back to the legacy menu:",
					err instanceof Error ? err.message : err
				);
				return [] as ResolvedMenuNode[];
			})
		]);
		const application = await getApplication(locale);
		norceStore.initState(application);

		const appData = {
			siteLogo: data.story.content.site_logo,
			siteLogoWhite:
				data.story.content.site_logo_white || data.story.content.site_logo,
			siteLogoDimensions: {
				width: Number(
					data.story.content.site_logo.filename.split("/")[5].split("x")[0]
				),
				height: Number(
					data.story.content.site_logo.filename.split("/")[5].split("x")[1]
				)
			},
			search: data.story.content.search,
			account: data.story.content.account,
			topNavbarLeft: data.story.content.top_navbar_left,
			menu,
			// Legacy menu, the fallback while `menu` is empty. Guarded like the base
			// useInitApp: editors may remove these blocks once MenuNode is live, and
			// an unguarded read would throw before initState runs.
			menuCategories: (data.story.content.menu_categories ?? []).filter(
				(menuCat: MenuLink) => menuCat.component !== "categories_multi_list"
			),
			menuCategoriesNorce: (() => {
				const norceIds = (data.story.content.menu_categories ?? []).filter(
					(menuCat: MenuLink) => menuCat.component === "categories_multi_list"
				)[0]?.categories;
				if (!norceIds?.length) return [];
				return getMenuCategoriesFromNorce({
					categoriesTree,
					norceMenuCategoriesFromStoryblok: norceIds
				});
			})(),
			menuTriggerType: data.story.content.menu_trigger_type ?? "click",
			applicationNorce: application,
			culturesNorce: getCulturesFromLocales(locales),
			currenciesNorce: norceStore.currenciesNorce,
			headerLoaded: true,
			flags: data.story.content.flags,
			topBar: data.story.content.top_bar?.[0] || null,
			lipscore_api_key: data.story.content.lipscore_api_key ?? null,
			lipscore_enable: data.story.content.lipscore_enable ?? null,
			bottomBar: data.story.content.bottom_bar?.[0] || null,
			stockStatus: data.story.content.stock_status,
			footer: extractFooterFields(data.story.content)
		};

		appStore.initState(appData);

		// Cart failure is isolated so a cart outage never blocks the product
		// catalogue. Parametrics failure still propagates.
		await parallelProductInit({
			getParametricsInformation,
			cartInit: initCart,
			productStore,
			storeContext: norceStore.storeContext,
			defaultImageFilename: data.story.content.default_image.filename,
			priceLists
		});
	};

	return {
		initApp
	};
};
